/* eslint-disable consistent-return */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';

import SpinnerOverlay from '../../components/SpinnerOverlay/SpinnerOverlay';
import CustomDateRangePicker from '../../components/DateRangePicker/DateRangePicker';
import { handleError, getInitials, extractFirstMentionedFullName } from '../../utils';
import useAxios from '../../hooks/useAxios';
import { useAuth } from '../../hooks/useAuth';

import './Home.scss';

import PendingActionIcon from '../../assets/icons/dashboard-pending-action-icon.svg';
import AgencySubmissionIcon from '../../assets/icons/agency-submissions-icon.svg';
import InReviewIcon from '../../assets/icons/in-review-icon.svg';
import OverdueWorkflowIcon from '../../assets/icons/overdue-workflows-icon.svg';
import SummaryPendingIcon from '../../assets/icons/status-icons/pending-icon.svg';
import ActiveWorkflowsIcon from '../../assets/icons/active-workflows-icon.svg';
import UnderDiscussionIcon from '../../assets/icons/under-discussion-icon.svg';
import ReviewCompletedIcon from '../../assets/icons/review-completed-icon.svg';
import TotalWorkflowIcon from '../../assets/icons/total-workflows-icon.svg';
import ReturnChangesIcon from '../../assets/icons/return-changes-icon.svg';

const DEFAULT_LAZY_STATE = {
  first: 0,
  rows: 10,
  page: 0,
  sortField: 'created_datetime',
  sortOrder: -1,
};

const getStoredLazyStates = (): Record<string, any> => {
  try {
    return JSON.parse(localStorage.getItem('dashbordSorting') || '{}') || {};
  } catch {
    return {};
  }
};

const getLazyStateForToggle = (toggle: string) => getStoredLazyStates()[toggle] ?? DEFAULT_LAZY_STATE;

const saveLazyStateForToggle = (toggle: string, state: any) => {
  const stored = getStoredLazyStates();
  stored[toggle] = state;
  localStorage.setItem('dashbordSorting', JSON.stringify(stored));
};

// Resets pagination for every tab's stored lazy state (not just the currently active tab),
// so switching tabs afterwards doesn't land back on a stale page number.
const resetAllStoredLazyStatePages = () => {
  const stored = getStoredLazyStates();
  Object.keys(stored).forEach((toggle) => {
    stored[toggle] = { ...stored[toggle], page: 0, first: 0 };
  });
  localStorage.setItem('dashbordSorting', JSON.stringify(stored));
};

const TIME_PERIOD_OPTIONS = [
  { name: '7 days', code: 7 },
  { name: '30 days', code: 30 },
  { name: '60 days', code: 60 },
  { name: '90 days', code: 90 },
  { name: 'Select Date', code: 'custom' }
];

const DEFAULT_TIME_PERIOD = { name: '30 days', code: 30 };

// Restores the last-selected "Last Updated On" option. The selected option
// itself (7/30/60/90/custom) is persisted separately from the resolved date
// range, so a preset always restores as its named option, and only a custom
// selection falls back to showing the resolved dates.
const getStoredTimePeriod = () => {
  try {
    const storedPeriod = localStorage.getItem('dashboardTimePeriod');
    const parsedPeriod = storedPeriod ? JSON.parse(storedPeriod) : null;

    if (parsedPeriod?.code && parsedPeriod.code !== 'custom') {
      const matched = TIME_PERIOD_OPTIONS.find((opt) => opt.code === parsedPeriod.code);
      if (matched) {
        return { timePeriodValue: matched, customDateRange: null };
      }
    }

    if (parsedPeriod?.code === 'custom') {
      const stored = localStorage.getItem('dashboardDateRange');
      const parsed = stored ? JSON.parse(stored) : null;
      if (parsed?.start && parsed?.end) {
        return {
          timePeriodValue: { name: 'Select Date', code: 'custom' },
          customDateRange: { startDate: new Date(parsed.start), endDate: new Date(parsed.end) },
        };
      }
    }

    return { timePeriodValue: DEFAULT_TIME_PERIOD, customDateRange: null };
  } catch {
    return { timePeriodValue: DEFAULT_TIME_PERIOD, customDateRange: null };
  }
};

const VerdictGraph = lazy(() => import('./VerdictGraph/VerdictGraph'));
const PendingWorkflowGraph = lazy(() => import('./PendingWorkflowGraph/PendingWorkflowGraph'));
const WorkflowTable = lazy(() => import('./WorkflowTable/WorkflowTable'));
const ActivityFeed = lazy(() => import('./ActivityFeed/ActivityFeed'));
const MentionList = lazy(() => import('./MentionList/MentionList'));

const Home = () => {
  const toast = useRef(null);
  const toastBottom = useRef(null);

  const { user } = useAuth();

  const reviewerRole = user.role === 'reviewer';
  const boAgencyRole = user.role === 'brand_owner' || user.role === 'agency';
  const adminRole = user.role === 'admin' || user.role === 'marcom';

  const defaultToggle = adminRole ? 'assigned_to_me'
    : reviewerRole
      ? 'pending_review'
      : 'active_workflows';

  const [isLoading, setisLoading] = useState<any>(false);
  const [filterTable, setFilterTable] = useState<any>('top_10');
  const [toggleFilterTable, setToggleFilterTable] = useState(localStorage.getItem('LastToggledOption') ?? defaultToggle);
  const [userActivityData, setUserActivityData] = useState<any>([]);
  const [filteredActivityData, setFilteredActivityData] = useState<any>([]);
  const [timePeriodValue, setTimePeriodValue] = useState<any>(() => getStoredTimePeriod().timePeriodValue);
  const [customDateRange, setCustomDateRange] = useState<any>(() => getStoredTimePeriod().customDateRange);
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    localStorage.setItem('dashboardTimePeriod', JSON.stringify({ code: timePeriodValue.code, name: timePeriodValue.name }));
  }, [timePeriodValue]);
  const [pendingReview, setPendingReview] = useState<any>(0);
  const [pendingWorkflowData, setPendingWorkflowData] = useState<any>([]);
  const [allActiveWorkflows, setAllActiveWorkflows] = useState<any>([]);
  const [allCompleteWorkflows, setAllCompleteWorkflows] = useState<any>([]);
  const [activeWorkflowsCount, setActiveWorkflowsCount] = useState<any>(0);
  const [completeWorkflowsCount, setCompleteWorkflowsCount] = useState<any>(0);
  const [allAssignedWorkflows, setAllAssignedWorkflows] = useState<any>([]);
  const [assignedWorkflowCount, setAssignedWorkflowCount] = useState<any>(0);

  const [summaryData, setSummaryData] = useState<any>([]);
  const [verdictGraphData, setVerdictGraphData] = useState<any>({});
  const [pendingWorkflowGraphData, setPendingWorkflowGraphData] = useState<any>([]);
  const [mentionData, setMentionData] = useState<any>([]);

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { get, post, apiLoading } = useAxios();

  const [activityFilterCategory, setActivityFilterCategory] = useState<any>('all');
  const [allWorkflows, setAllWorkflows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyState, setlazyState] = useState(() => getLazyStateForToggle(toggleFilterTable));

  let sortOrder = 'DESC';
  if (lazyState.sortOrder && lazyState.sortOrder === 1) {
    sortOrder = 'ASC';
  }
  const prevSortRef = useRef({
    sortField: lazyState.sortField,
    sortOrder: lazyState.sortOrder,
  });

  const onSort = (event: any) => {
    setlazyState((prevState) => ({
      ...prevState,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    }));
  };

  const timePeriodOptions = TIME_PERIOD_OPTIONS;

  const activityFilterOptions = [
    {
      name: 'All',
      value: 'all',
      category: [],
      action: []
    },
    {
      name: 'Annotations',
      value: 'annotations',
      category: ['annotation_file', 'file_attachment'],
      action: ['update_data', 'add_data', 'delete_data']
    },
    {
      name: 'Annotation Status',
      value: 'annotation_status',
      category: ['file_attachment'],
      action: ['status_change']
    },
    {
      name: 'Comments',
      value: 'comments',
      category: ['annotation_file_comment', 'file_attachment_comment'],
      action: ['add_data', 'delete_data']
    },
    {
      name: 'Materials Uploaded',
      value: 'materials_uploaded',
      category: ['annotation_file', 'file_attachment', 'workflow_asset'],
      action: ['upload']
    },
    {
      name: 'Materials Deleted',
      value: 'materials_deleted',
      category: ['workflow_asset', 'annotation_file'],
      action: ['delete']
    },
    {
      name: 'Materials Sent for Approval',
      value: 'materials_sent_for_approval',
      category: ['annotation_file', 'file_attachment'],
      action: []
    },
    {
      name: 'Verdict/Voting',
      value: 'verdict_voting',
      category: ['file_attachment'],
      action: ['vote']
    },
    {
      name: 'Workflow Status',
      value: 'workflow_status',
      category: ['workflow'],
      action: ['status_change']
    },
    {
      name: 'Agency Actions',
      value: 'agency_actions',
      category: ['annotation_file'],
      action: ['upload']
    },
    {
      name: 'Assets',
      value: 'assets',
      category: ['file_attachment'],
      action: []
    },
    {
      name: 'Workflow Updates',
      value: 'workflow_updates',
      category: ['workflow'],
      action: []
    }
  ];

  const onActivityFilterCategoryChange = (selected: any) => {
    if (selected === 'all') {
      setFilteredActivityData(userActivityData);
      setActivityFilterCategory('all');
    } else {
      const selectedOption: any = activityFilterOptions.find(opt => opt.value === selected);

      setActivityFilterCategory(selected);

      const tempData = userActivityData.filter((data: any) => {
        const matchesCategory = selectedOption.category.includes(data.category);
        const matchesAction = selectedOption.action.length === 0 || (data.action && selectedOption.action.includes(data.action));

        return matchesCategory && matchesAction;
      });

      setFilteredActivityData(tempData);
    }
  };

  const getUserBatch = (ids: any) => {
    const postData = {
      id_list: ids
    };

    try {
      const res = post('user-manager/batch_get_user_details', postData).then((response: any) => {
        const { status } = response;

        if ([200, 201].includes(status)) {
          if (response.data.messages && response.data.messages.length > 0) {
            return response.data.messages;
          }
        }
        return [];
      }).catch((error: any) => {
        if ([401, 404, 500].includes(error?.request?.status)) {
          handleError(error, toastBottom);
        }
        return [];
      });

      return res;
    } catch (error: any) {
      if ([401, 404, 500].includes(error?.request?.status)) {
        handleError(error, toastBottom);
      }
      return Promise.resolve([]);
    }
  };

  const onTogglePageChange = (val: any) => {
    setToggleFilterTable(val);
    localStorage.setItem('LastToggledOption', val);
    setlazyState(getLazyStateForToggle(val));
  };

  const toDateOnly = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatCustomDateRange = (range: any) => ({
    start: toDateOnly(new Date(range.startDate)),
    end: toDateOnly(new Date(range.endDate)),
  });

  const formatDateForDisplay = (date: Date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const isCustomRangeValid = timePeriodValue.code === 'custom' && customDateRange?.startDate && customDateRange?.endDate;

  const getAllData = async (isSort = false, isWorkflowOnly = false) => {
    if (timePeriodValue.code === 'custom' && !isCustomRangeValid) {
      return;
    }

    setisLoading(true);

    let apiPath = `/ai-search-manager/cosmos_db_get_dashboard_by_user_id?results_per_page=${lazyState.rows}&page_number=${lazyState.page + 1}&order_by=c.${lazyState.sortField} ${sortOrder}&timezone=${userTimeZone}`;

    if (isCustomRangeValid) {
      const { start, end } = formatCustomDateRange(customDateRange);
      apiPath += `&from_date=${start}&to_date=${end}`;
    } else {
      apiPath += `&number_of_days=${timePeriodValue.code}`;
    }

    if (isSort) {
      apiPath += '&workflows_only=true';
    }
    if (['marcom', 'admin'].includes(user.role)) {
      apiPath += '&get_all_if_admin=true';
    }
    if (isWorkflowOnly) {
      apiPath += '&workflow_list_only=true';
    }

    await get(apiPath).then((response: any) => {
      const { status } = response;
      setisLoading(false);
      saveLazyStateForToggle(toggleFilterTable, lazyState);
      if ([200, 201].includes(status)) {
        // getting top summary data
        const summaryResponse = response?.data?.messages[0];
        if (summaryResponse?.start_date && summaryResponse?.end_date) {
          localStorage.setItem('dashboardDateRange', JSON.stringify({ start: summaryResponse.start_date, end: summaryResponse.end_date }));
        }
        if (!isWorkflowOnly) {
          if (['marcom', 'admin'].includes(user.role)) {
            const tempData = [
              { label: 'Active Workflows', value: summaryResponse?.active_workflows?.total, icon: ActiveWorkflowsIcon, className: 'active-workflows-card', filterType: 'active' },
              { label: 'Overdue Workflows', value: summaryResponse?.overdue_workflows.total, icon: SummaryPendingIcon, className: 'pending-workflow-card', filterType: 'overdue' },
              { label: 'Non Compliant Workflows', value: summaryResponse?.non_compliant_workflows.total, icon: InReviewIcon, className: 'in-review-card', filterType: 'non_compliant' },
              { label: 'Needs Meeting', value: summaryResponse?.materials_under_discussion.total, icon: UnderDiscussionIcon, className: 'under-discussion-card', filterType: 'needs_meeting' },
            ];
            setSummaryData(tempData);
          } else if (['brand_owner'].includes(user.role)) {
            const tempData = [
              { label: 'Pending Action', value: summaryResponse?.pending_action.total, icon: PendingActionIcon, className: 'pending-action-card', filterType: 'pending_action_bo' },
              { label: 'Agency Submissions', value: summaryResponse?.agency_submissions.total, icon: AgencySubmissionIcon, className: 'agency-submissions-card', filterType: 'agency_submissions' },
              { label: 'In Review', value: summaryResponse?.in_review.total, icon: InReviewIcon, className: 'in-review-card', filterType: 'in_review' },
              { label: 'Overdue Workflows', value: summaryResponse?.overdue_workflows.total, icon: OverdueWorkflowIcon, className: 'overdue-workflow-card', filterType: 'overdue' },
            ];
            setSummaryData(tempData);
          } else if (['reviewer'].includes(user.role)) {
            const tempData = [
              { label: 'Review Completed', value: summaryResponse?.review_completed.total, icon: ReviewCompletedIcon, className: 'review-completed-card', filterType: 'review_completed' },
              { label: 'Pending Action', value: summaryResponse?.pending_action.total, icon: PendingActionIcon, className: 'pending-action-card', filterType: 'pending_action_reviewer' },
              { label: 'Overdue Workflows', value: summaryResponse?.overdue_workflows.total, icon: SummaryPendingIcon, className: 'pending-workflow-card', filterType: 'overdue' },
            ];
            setSummaryData(tempData);
          } else if (['agency'].includes(user.role)) {
            const tempData = [
              { label: 'Total Workflow', value: summaryResponse?.total_workflows.total, icon: TotalWorkflowIcon, className: 'review-completed-card', filterType: 'total' },
              { label: 'Draft Workflows', value: summaryResponse?.draft_workflows.total, icon: PendingActionIcon, className: 'pending-action-card', filterType: 'draft' },
              { label: 'Returned for Changes', value: summaryResponse?.returned_for_changes_workflows.total, icon: ReturnChangesIcon, className: 'pending-workflow-card', filterType: 'returned_for_changes' },
            ];
            setSummaryData(tempData);
          } else {
            const tempData = [
              { label: 'Pending Action', value: 120, icon: PendingActionIcon, className: 'pending-action-card', filterType: 'pending_action_bo' },
              { label: 'Agency Submissions', value: 20, icon: AgencySubmissionIcon, className: 'agency-submissions-card', filterType: 'agency_submissions' },
              { label: 'In Review', value: 18, icon: InReviewIcon, className: 'in-review-card', filterType: 'in_review' },
              { label: 'Overdue Workflows', value: 6, icon: OverdueWorkflowIcon, className: 'overdue-workflow-card', filterType: 'overdue' },
            ];
            setSummaryData(tempData);
          }
        }

        // getting verdict graph data
        if (summaryResponse.material_verdicts) {
          summaryResponse.material_verdicts = Object.fromEntries(
            Object.entries(summaryResponse.material_verdicts).map(([key, value]) => [
              key === 'in_discussion' ? 'needs_meeting' : key,
              value
            ])
          );
          setVerdictGraphData(summaryResponse.material_verdicts);
        }

        // getting pending workflow graph data
        if (summaryResponse.pending_workflows_per_reviewer) {
          const tempRevs = Object.keys(summaryResponse.pending_workflows_per_reviewer);
          if (tempRevs && tempRevs.length > 0) {
            getUserBatch(tempRevs)?.then((res: any) => {
              const updatedData: any = Object.entries(summaryResponse.pending_workflows_per_reviewer).map(([id, value]: any) => {
                const tempUser = res?.find((u: any) => u.id === id);
                const fullName = tempUser ? `${tempUser.first_name} ${tempUser.last_name}` : id;

                return {
                  id,
                  fullName,
                  total: value.total
                };
              }).sort((a: any, b: any) => b.total - a.total);

              setPendingWorkflowGraphData(updatedData);
            });
          } else {
            setPendingWorkflowGraphData([]);
          }
        }

        // Workflow Table Data for admin/marcom
        if (adminRole) {
          // Other Workflow
          if (summaryResponse.other_workflows) {
            setTotalRecords(summaryResponse.other_workflows.total_items);
            let tempUsers = summaryResponse.other_workflows.workflows.map((a: any) => a.created_by_user_id);
            if (tempUsers && tempUsers.length > 0) {
              getUserBatch(tempUsers)?.then((res: any) => {
                let tempWorkflows: any = [];
                summaryResponse.other_workflows.workflows.forEach((element: any, i: any) => {
                  tempWorkflows.push({
                    ...element,
                    fullName: `${res[i]?.first_name} ${res[i]?.last_name}`,
                    nameInitials: getInitials(`${res[i]?.first_name} ${res[i]?.last_name}`)
                  });
                });
                setAllWorkflows(tempWorkflows);
              });
            }
          }

          // assigned to me workflows
          if (summaryResponse.non_reviewer_workflows) {
            setAssignedWorkflowCount(summaryResponse.non_reviewer_workflows.total_items);
            let tempUsers = summaryResponse.non_reviewer_workflows.workflows.map((a: any) => a.created_by_user_id);
            if (tempUsers && tempUsers.length > 0) {
              getUserBatch(tempUsers)?.then((res: any) => {
                let tempWorkflows: any = [];
                summaryResponse.non_reviewer_workflows.workflows.forEach((element: any, i: any) => {
                  tempWorkflows.push({
                    ...element,
                    fullName: `${res[i]?.first_name} ${res[i]?.last_name}`,
                    nameInitials: getInitials(`${res[i]?.first_name} ${res[i]?.last_name}`)
                  });
                });
                setAllAssignedWorkflows(tempWorkflows);
              });
            }
          }
        }

        // Workflow Table Data for Project Owner and Agency
        if (boAgencyRole) {
          // Active Workflow
          if (summaryResponse.active_workflows) {
            setActiveWorkflowsCount(summaryResponse.active_workflows.total_items);
            let tempUsers = summaryResponse.active_workflows.workflows.map((a: any) => a.created_by_user_id);
            if (tempUsers && tempUsers.length > 0) {
              getUserBatch(tempUsers)?.then((res: any) => {
                let tempWorkflows: any = [];
                summaryResponse.active_workflows.workflows.forEach((element: any, i: any) => {
                  tempWorkflows.push({
                    ...element,
                    fullName: `${res[i]?.first_name} ${res[i]?.last_name}`,
                    nameInitials: getInitials(`${res[i]?.first_name} ${res[i]?.last_name}`)
                  });
                });
                if (user.role === 'brand_owner') {
                  const excludedStatuses = new Set(['draft', 'draft_initial', 'r&r_re_upload', 'awc_final_upload', 'send_to_po_final_upload', 'send_to_po_initial']);
                  tempWorkflows = tempWorkflows.filter((w: any) => !excludedStatuses.has(w.status));
                }
                setAllActiveWorkflows(tempWorkflows);
              });
            }
          }

          // Completed Workflow
          if (summaryResponse.complete_workflows) {
            setCompleteWorkflowsCount(summaryResponse.complete_workflows.total_items);
            let tempUsers = summaryResponse.complete_workflows.workflows.map((a: any) => a.created_by_user_id);
            if (tempUsers && tempUsers.length > 0) {
              getUserBatch(tempUsers)?.then((res: any) => {
                let tempWorkflows: any = [];
                summaryResponse.complete_workflows.workflows.forEach((element: any, i: any) => {
                  tempWorkflows.push({
                    ...element,
                    fullName: `${res[i]?.first_name} ${res[i]?.last_name}`,
                    nameInitials: getInitials(`${res[i]?.first_name} ${res[i]?.last_name}`)
                  });
                });
                setAllCompleteWorkflows(tempWorkflows);
              });
            }
          }
        }

        // Workflow Table Data for Reviewer
        if (reviewerRole) {
          // All Workflow
          if (summaryResponse.all_workflows) {
            setTotalRecords(summaryResponse.all_workflows.total_items);
            let tempUsers = summaryResponse.all_workflows.workflows.map((a: any) => a.created_by_user_id);

            if (tempUsers && tempUsers.length > 0) {
              getUserBatch(tempUsers)?.then((res: any) => {
                let tempWorkflows: any = [];
                summaryResponse.all_workflows.workflows.forEach((element: any, i: any) => {
                  tempWorkflows.push({
                    ...element,
                    fullName: `${res[i]?.first_name} ${res[i]?.last_name}`,
                    nameInitials: getInitials(`${res[i]?.first_name} ${res[i]?.last_name}`)
                  });
                });
                setAllWorkflows(tempWorkflows);
              });
            }
          }

          // Pending Action Workflow
          if (summaryResponse.pending_action_workflows) {
            setPendingReview(summaryResponse.pending_action_workflows.total_items);
            let tempUsers = summaryResponse.pending_action_workflows.workflows.map((a: any) => a.created_by_user_id);

            if (tempUsers && tempUsers.length > 0) {
              getUserBatch(tempUsers)?.then((res: any) => {
                let tempWorkflows: any = [];
                summaryResponse.pending_action_workflows.workflows.forEach((element: any, i: any) => {
                  tempWorkflows.push({
                    ...element,
                    fullName: `${res[i]?.first_name} ${res[i]?.last_name}`,
                    nameInitials: getInitials(`${res[i]?.first_name} ${res[i]?.last_name}`)
                  });
                });
                setPendingWorkflowData(tempWorkflows);
              });
            }
          }
        }

        // Activity Feed
        if (response.data.messages[2] && response.data.messages[2].length > 0) {
          let tempActivities: any = [];
          response.data.messages[2].forEach((element: any, i: any) => {
            let tempFullName = extractFirstMentionedFullName(element?.audit_log_message, element.at_mentions);
            tempActivities.push({
              ...element,
              fullName: tempFullName,
              nameInitials: tempFullName ? getInitials(tempFullName) : '',
            });
          });

          setUserActivityData(tempActivities);
          setFilteredActivityData(tempActivities);
        }

        // Mention List Data
        if (!adminRole && response.data.messages[3] && response.data.messages[3].length > 0) {
          let tempMentions: any = [];
          response.data.messages[3].forEach((element: any, i: any) => {
            let tempFullName = extractFirstMentionedFullName(element?.audit_log_message, element.at_mentions);
            tempMentions.push({
              ...element,
              fullName: tempFullName,
              nameInitials: tempFullName ? getInitials(tempFullName) : '',
            });
          });
          setMentionData(tempMentions);
        }

        if (response.data.messages.total_items === 0) {
          setPendingReview(0);
          setAllWorkflows([]);
          setUserActivityData([]);
          setFilteredActivityData([]);
        }
      }
    }).catch((error: any) => {
      if ([401, 404, 500].includes(error?.request?.status)) {
        handleError(error, toastBottom);
      }

      setisLoading(false);
    });
  };

  const onPage = (event: any) => {
    setlazyState(event);
  };

  const timePeriodChangedRef = useRef(false);
  const prevPageRef = useRef(lazyState.page);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      getAllData();
      return;
    }
    if (timePeriodValue.code === 'custom' && !isCustomRangeValid) {
      return;
    }
    timePeriodChangedRef.current = true;
    setlazyState((prev) => ({ ...prev, page: 0 }));
  }, [timePeriodValue, customDateRange]);

  const prevFilterRef = useRef(filterTable);

  useEffect(() => {
    const isFilterChanged = prevFilterRef.current !== filterTable;

    if (isFilterChanged) {
      getAllData();
    }

    prevFilterRef.current = filterTable;
  }, [filterTable, lazyState.page]);

  const prevToggleRef = useRef(toggleFilterTable);

  useEffect(() => {
    const isSortChanged = prevSortRef.current.sortField !== lazyState.sortField
      || prevSortRef.current.sortOrder !== lazyState.sortOrder;

    const isPageChanged = prevPageRef.current !== lazyState.page;

    const isToggleChanged = prevToggleRef.current !== toggleFilterTable;

    const shouldFetch = timePeriodChangedRef.current || isSortChanged || isPageChanged || isToggleChanged;

    if (shouldFetch) {
      if (isSortChanged || isPageChanged || isToggleChanged) {
        getAllData(isSortChanged, true);
      } else {
        getAllData(isSortChanged);
      }
      timePeriodChangedRef.current = false;
      prevPageRef.current = lazyState.page;
    }

    prevSortRef.current = {
      sortField: lazyState.sortField,
      sortOrder: lazyState.sortOrder,
    };
    prevToggleRef.current = toggleFilterTable;
  }, [lazyState, timePeriodValue, toggleFilterTable]);

  useEffect(() => {
    prevSortRef.current = {
      sortField: lazyState.sortField,
      sortOrder: lazyState.sortOrder,
    };
  }, [lazyState]);

  const onBrandGuidelines = () => {
    window.open('https://identitynet.bayer.com/portal/', '_blank', 'noopener,noreferrer');
  };

  const onSummaryCardClick = (filterType: string) => {
    if (!filterType) {
      return;
    }
    localStorage.removeItem('WorkflowFilter');
    localStorage.removeItem('workflowSort');
    localStorage.setItem('LastDashboardCardClick', filterType);
    window.open('/workflows/list', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="home-container" role="home">
      <Toast ref={toast} />
      <Toast ref={toastBottom} position="bottom-right" />

      {/*
      {isLoading && (
        <div className="card">
          <SpinnerOverlay />
        </div>
      )}
      */}

      <div className="dashboard-header-section flex justify-content-between align-items-center">
        <div>
          <h2 className="homepage-title mb-0 mt-0">Dashboard</h2>
        </div>
        <div className="flex align-items-center gap-3">
          {['agency'].includes(user.role) && (
            <div className="brand-guidelines" onClick={() => onBrandGuidelines()}>
              Brand Guidelines
            </div>
          )}
         
          <div className="time-period-filter">
            <label className="time-period-filter-label">Last Updated On</label>
            <Dropdown
              value={timePeriodValue}
              options={timePeriodOptions}
              onChange={(e) => {
                if (reviewerRole) {
                  setToggleFilterTable('pending_review');
                } else if (boAgencyRole) {
                  setToggleFilterTable('active_workflows');
                } else if (adminRole) {
                  setToggleFilterTable('assigned_to_me');
                }
                if (e.value.code === 'custom') {
                  setShowDatePicker(true);
                } else {
                  setCustomDateRange(null);
                }
                setTimePeriodValue(e.value);
                resetAllStoredLazyStatePages();
                setlazyState((prevState: any) => ({
                  ...prevState,
                  page: 0
                }));
              }}
              onShow={() => {
                if (timePeriodValue.code === 'custom') {
                  setShowDatePicker(true);
                }
              }}
              placeholder="Time period"
              optionLabel="name"
              appendTo="self"
              valueTemplate={() => (
                isCustomRangeValid
                  ? `${formatDateForDisplay(new Date(customDateRange.startDate))} - ${formatDateForDisplay(new Date(customDateRange.endDate))}`
                  : timePeriodValue.name
              )}
            />
          </div>
          {timePeriodValue.code === 'custom' && showDatePicker && (
            <CustomDateRangePicker
              hideTrigger
              autoOpen
              centerOnScreen
              disableFutureDates
              hideInfoIcon
              placeholder="Select Date Range"
              value={customDateRange || { startDate: null, endDate: null }}
              onCancel={() => setShowDatePicker(false)}
              onChange={(range) => {
                setShowDatePicker(false);
                if (!range?.startDate && !range?.endDate) {
                  setCustomDateRange(null);
                  setTimePeriodValue({ name: '30 days', code: 30 });
                } else {
                  setCustomDateRange(range);
                }
                resetAllStoredLazyStatePages();
                setlazyState((prevState: any) => ({
                  ...prevState,
                  page: 0
                }));
              }}
            />
          )}
        </div>
      </div>

      {['admin', 'marcom', 'brand_owner'].includes(user.role) && (
        <div className="dashboard-top-section flex">
          {summaryData.map((stat: any, i: any) => (
            <div
              key={i}
              className={`flex-1 dashboard-top-box justify-content-between align-items-center ${stat.className}`}
              style={{ cursor: 'pointer' }}
              role="button"
              tabIndex={0}
              onClick={() => onSummaryCardClick(stat.filterType)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onSummaryCardClick(stat.filterType);
                }
              }}
            >
              <div style={{ flexBasis: '77%' }}>
                <a
                  className="top-box-value"
                  href="/workflows/list"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSummaryCardClick(stat.filterType);
                  }}
                >
                  {stat.value}
                </a>
                <div className="top-box-status">{stat.label}</div>
              </div>
              <div className="top-box-icon-container flex justify-content-end" style={{ flexBasis: '20%' }}>
                <div className="top-box-icon">
                  <img src={stat.icon} alt="icon" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="dashboard-main-content">
        <div className="dashboard-main-left">
          {['reviewer', 'agency'].includes(user.role) && (
            <div className="dashboard-top-section flex mt-0" style={{ marginBottom: '20px' }}>
              {summaryData.map((stat: any, i: any) => (
                <div
                  key={i}
                  className={`flex-1 dashboard-top-box justify-content-between align-items-center ${stat.className}`}
                  style={{ cursor: 'pointer' }}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSummaryCardClick(stat.filterType)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onSummaryCardClick(stat.filterType);
                    }
                  }}
                >
                  <div style={{ flexBasis: '77%' }}>
                    <a
                      className="top-box-value"
                      href="/workflows/list"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSummaryCardClick(stat.filterType);
                      }}
                    >
                      {stat.value}
                    </a>
                    <div className="top-box-status">{stat.label}</div>
                  </div>
                  <div className="top-box-icon-container flex justify-content-end" style={{ flexBasis: '20%' }}>
                    <div className="top-box-icon">
                      <img src={stat.icon} alt="icon" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="dashboard-card">
            <Suspense fallback={<SpinnerOverlay />}>
              <WorkflowTable
                pendingReview={pendingReview}
                totalRecords={totalRecords}
                assignedWorkflowCount={assignedWorkflowCount}
                activeWorkflowsCount={activeWorkflowsCount}
                completeWorkflowsCount={completeWorkflowsCount}
                toggleFilterTable={toggleFilterTable}
                allAssignedWorkflows={allAssignedWorkflows}
                pendingWorkflowData={pendingWorkflowData}
                allActiveWorkflows={allActiveWorkflows}
                allCompleteWorkflows={allCompleteWorkflows}
                allWorkflows={allWorkflows}
                lazyState={lazyState}
                onTogglePageChange={onTogglePageChange}
                onPage={onPage}
                onSort={onSort}
                apiLoading={apiLoading}
              />
            </Suspense>
          </div>
          {!['admin', 'marcom', 'reviewer'].includes(user.role) && (
            <div className="dashboard-card">
              <Suspense fallback={<SpinnerOverlay />}>
                <MentionList mentionListData={mentionData} apiLoading={apiLoading} />
              </Suspense>
            </div>
          )}
        </div>
        <div className="dashboard-main-right">
          {['reviewer'].includes(user.role) && (
            <div className="dashboard-card">
              <Suspense fallback={<SpinnerOverlay />}>
                <MentionList mentionListData={mentionData} apiLoading={apiLoading} />
              </Suspense>
            </div>
          )}

          {['admin', 'marcom', 'brand_owner', 'agency'].includes(user.role) && (
            <div className="dashboard-card mb-15">
              <Suspense fallback={<SpinnerOverlay />}>
                <VerdictGraph graphData={verdictGraphData} apiLoading={apiLoading} />
              </Suspense>
            </div>
          )}
          {['admin', 'marcom'].includes(user.role) && (
            <div className="dashboard-card mb-15">
              <Suspense fallback={<SpinnerOverlay />}>
                <PendingWorkflowGraph graphData={pendingWorkflowGraphData} apiLoading={apiLoading} />
              </Suspense>
            </div>
          )}
          {!['admin', 'marcom'].includes(user.role) && (
            <div className="dashboard-card mb-15">
              <Suspense fallback={<SpinnerOverlay />}>
                <ActivityFeed
                  filteredActivityData={filteredActivityData}
                  activityFilterOptions={activityFilterOptions}
                  activityFilterCategory={activityFilterCategory}
                  onActivityFilterCategoryChange={onActivityFilterCategoryChange}
                  apiLoading={apiLoading}
                />
              </Suspense>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
